import axios from 'axios';

import { NotificationStoreState } from '~/widgets/NotificationList/model/notificationStore';
import { TokenResponse } from '~/shared/API/response-types/token.types';
import Cookies from 'js-cookie';
import { CurrentUserResponse } from '~/shared/API/response-types/currentUser.types';
import {
  Parking,
  ParkingType,
} from '~/shared/API/response-types/parkings.types';
import { ParkingSpacesResponse } from '~/shared/API/response-types/parking-spaces.types';
import { ActiveRentType } from './response-types/rent-types';

const API_URL = import.meta.env.VITE_REMIX_PUBLIC_FRONT_API_URL;

const instance = axios.create({
  baseURL: API_URL,
  headers: { 'X-Custom-Header': 'foobar' },
});

instance.interceptors.request.use(
  (config) => {
    const token = Cookies.get('token');
    if (token) {
      config.headers['Authorization'] = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

export const API = {
  async getPassword({ phone }: { phone: string }) {
    return instance.post('/auth/send-one-time-password', { phone });
  },
  async login({ phone, password }: { phone: string; password: string }) {
    return instance.post<TokenResponse>('/auth/login?returnUser=1', {
      phone,
      'one-time-password': password,
    });
  },
  async updateUser(body: {
    first_name: string;
    second_name: string;
    referral?: string;
  }) {
    return instance.post('/account', { ...body });
  },

  async getCurrentUser() {
    return instance.get<CurrentUserResponse>('/account/me');
  },

  async getLkInfo() {
    return instance.get<{ notification_unread_count: number; income: number }>(
      '/lk'
    );
  },

  async logout() {
    return instance.patch('/account/logout');
  },

  // автомобили

  async getCars() {
    return instance.get('/account/me/autos');
  },

  async addCar(number: string, name: string) {
    return instance.post('/account/me/autos', { number, name });
  },

  async editCar(number: string, name: string, id: string) {
    return instance.patch(`/account/me/autos/${id}`, { number, name });
  },

  async deleteCar(id: string) {
    return instance.delete(`/account/me/autos/${id}`);
  },

  // банковские карты

  async getCards() {
    return instance.get('/account/me/credit-cards');
  },

  async addCard() {
    return instance.post('/v1/payments/create-card-token');
  },

  async setActiveCard(id: number) {
    return instance.post(`/account/me/credit-cards/${id}/set-to-pay`);
  },

  async deleteCard(id: string) {
    return instance.delete(`/account/me/credit-cards/${id}`);
  },

  // платежи

  async getPaymentInfo(id: string) {
    return instance.get(`/v1/payments/${id}`);
  },

  // аренды

  async createSession(id: string | number, auto_id: number) {
    return instance.post<{
      payload: Parking;
    }>(`/rents/${id}/start`, {
      auto_renewal: true,
      auto_id: auto_id,
    });
  },

  // async paySession(id: string | number) {
  //   return instance.get<{
  //     payload: Parking;
  //   }>(`/rent-sessions/${id}/pay`);
  // },

  async paySession(rentId: string | number, cardId?: string | number) {
    return instance.post<{
      payload: Parking;
    }>(`/v1/pay`, { rent_session_id: rentId, account_card_id: cardId });
  },

  async finishSession(id: string | number) {
    return instance.get<{
      payload: Parking;
    }>(`/rent-sessions/${id}/complete`);
  },

  async getActiveRents() {
    return instance.get<ActiveRentType>('/account/activeRents');
  },

  async getPastRents(type: 'in_progress' | 'completed' | 'payed') {
    return instance.get<ActiveRentType>(`/lk/rentSessions?type=${type}`);
  },

  // парковки
  async getParking() {
    return instance.get<ParkingType>('/parkings');
  },
  async getParkingById(id: string | number) {
    return instance.get<{
      payload: Parking;
    }>(`/parkings/${id}`);
  },
  async getParkingByAddress(address: string) {
    return instance.get<ParkingType>(
      `/parkings/find-by-address?address=${address}`
    );
  },
  async getParkingFormData() {
    return instance.get<ParkingType>('/parkings/form-data');
  },
  async createParking(body: {
    name: string;
    description: string;
    max_height: string;
    address: string;
    construction_type: string;
    type: string;
    location_type: string;
    lat: number;
    lon: number;
    is_around_the_clock: boolean;
    work_start: string;
    work_end: string;
    cars_count: number;
    price_text: string;
    tags: number[];
  }) {
    return instance.post<unknown>(`/parkings`, { ...body });
  },

  // парковочные места
  async getParkingSpacesById(
    id: string | number,
    params?: Record<string, any>
  ) {
    return instance.get<ParkingSpacesResponse>(`/parkings/${id}/spaces`, {
      params,
    });
  },
  async createMyParkingSpace(
    id: string | number,
    body: {
      number: string;
      rate: { day_price: number; week_price: number; month_price: number };
    }
  ) {
    instance.defaults.headers.common['Content-Type'] = 'multipart/form-data';
    return instance.post<unknown>(`/parkings/${id}/spaces`, body);
  },
  async getMyPlaces() {
    return instance.get<unknown>(`/spaces/my`);
  },
  async getMyPlace(id: string | number) {
    return instance.get<unknown>(`/spaces/${id}`);
  },
  async updateMyPlace(id: string | number) {
    return instance.put<unknown>(`/spaces/${id}`);
  },
  async deleteMyPlace(id: string | number) {
    return instance.delete<unknown>(`/spaces/${id}`);
  },

  // уведомления
  async getNotifications() {
    return instance.get<NotificationStoreState[]>('/notifications');
  },
  async getNewNotifications() {
    return instance.get<NotificationStoreState[]>('/notifications/new');
  },
};
